// Hometown shown as "City, ST". The frontend twin of
// backend/users/athlete_identity.py (split_hometown, state_abbr,
// format_hometown): same rules, same US state list. The backend identity
// block is the source of truth on profile pages; this twin covers client
// only paths (a missing identity, an editor splitting a typed state off).

// One US state list: 50 states plus DC, name -> postal code.
export const US_STATES = Object.freeze({
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR",
  California: "CA", Colorado: "CO", Connecticut: "CT", Delaware: "DE",
  "District of Columbia": "DC", Florida: "FL", Georgia: "GA", Hawaii: "HI",
  Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME",
  Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE",
  Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM",
  "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH",
  Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
  "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX",
  Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA",
  "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
});

// The same list as [{ name, abbr }], for selects.
export const US_STATE_LIST = Object.freeze(
  Object.entries(US_STATES).map(([name, abbr]) => Object.freeze({ name, abbr }))
);

const NAME_TO_ABBR = Object.fromEntries(Object.entries(US_STATES).map(([n, a]) => [n.toLowerCase(), a]));
const ABBR_TO_NAME = Object.fromEntries(Object.entries(US_STATES).map(([n, a]) => [a, n]));
// Longest first, so "West Virginia" is tried before "Virginia".
const NAMES_LONGEST_FIRST = Object.keys(US_STATES).sort((a, b) => b.length - a.length);
// Words that make "<word> <state>" a region of that state ("Northern
// California", "West Texas"): the state is never split off it, and it is
// not repeated after it.
const REGION_WORDS = new Set([
  "north", "south", "east", "west", "central", "northern", "southern", "eastern",
  "western", "greater", "upper", "lower", "northeast", "northwest", "southeast",
  "southwest", "northeastern", "northwestern", "southeastern", "southwestern",
]);
// A trailing state (name or code) is not split off when only one of these
// words would be left: "Port Washington" style places and the regions above
// stay as typed.
const PLACE_PREFIXES = new Set([
  ...REGION_WORDS,
  "port", "fort", "ft", "mount", "mt", "lake", "new", "old", "little", "big",
  "st", "saint", "san", "santa", "los", "las", "el", "la", "de",
]);
const COUNTRY_SUFFIXES = ["usa", "u.s.a.", "u.s.a", "united states", "united states of america"];

const stripEdges = (s) => s.replace(/^[\s,]+|[\s,]+$/g, "");

const collapse = (raw) => {
  if (raw == null || typeof raw === "object") return "";
  return stripEdges(String(raw).split(/\s+/).filter(Boolean).join(" "));
};

const isZip = (token) => /^\d{5}(-\d{4})?$/.test(token);

// "Port", "West", "St.": a word a trailing state is never split from.
const isPrefix = (head) => PLACE_PREFIXES.has(head.replace(/\./g, "").trim().toLowerCase());

// Drop a trailing ZIP and a trailing USA / US / United States, as many
// times as they repeat ("Las Vegas, Nevada, USA 89101").
const dropTrailingNoise = (input) => {
  let text = input;
  for (;;) {
    text = text.replace(/[\s,]+$/, "");
    const cut = Math.max(text.lastIndexOf(" "), text.lastIndexOf(","));
    if (cut < 0) return text;
    const last = text.slice(cut + 1);
    if (isZip(last) || last === "US" || last === "U.S." || last === "U.S") {
      text = text.slice(0, cut);
      continue;
    }
    const low = text.toLowerCase();
    const suffix = COUNTRY_SUFFIXES.find(
      (s) => low.endsWith(s) && low.length > s.length && " ,".includes(low[low.length - s.length - 1])
    );
    if (suffix) {
      text = text.slice(0, text.length - suffix.length);
      continue;
    }
    return text;
  }
};

// "AZ" (or "az" when anyCase, or "D.C.") -> the US postal code, else "".
const abbrOfToken = (token, anyCase = true) => {
  const key = String(token || "").replace(/\./g, "").trim();
  if (!/^[A-Za-z]{2}$/.test(key)) return "";
  if (!anyCase && key !== key.toUpperCase()) return "";
  const up = key.toUpperCase();
  return ABBR_TO_NAME[up] ? up : "";
};

// A US state name or postal code in any case -> "AZ", else "".
const stateOfText = (text) => {
  const t = String(text || "").split(/\s+/).filter(Boolean).join(" ");
  return NAME_TO_ABBR[t.toLowerCase()] || abbrOfToken(t);
};

// Split free hometown text into { city, state }. See split_hometown in
// athlete_identity.py for the rules; "Chandler Arizona" -> Chandler / AZ,
// "Toronto, ON" and "West TX" are kept as typed, a bare "New York" is the
// state alone (hometownParts reads it as a town when a different state is
// stored: "Washington" + PA).
export function splitHometown(raw) {
  const text = dropTrailingNoise(collapse(raw));
  if (!text) return { city: "", state: "" };
  const whole = NAME_TO_ABBR[text.toLowerCase()];
  if (whole) return { city: "", state: whole };
  if (text.includes(",")) {
    const idx = text.lastIndexOf(",");
    const head = stripEdges(text.slice(0, idx));
    const st = stateOfText(text.slice(idx + 1));
    if (st && head) return { city: head, state: st };
    return { city: text, state: "" };
  }
  const low = text.toLowerCase();
  for (const name of NAMES_LONGEST_FIRST) {
    const n = name.toLowerCase();
    if (low.endsWith(` ${n}`)) {
      const head = stripEdges(text.slice(0, text.length - n.length));
      if (head && !isPrefix(head)) {
        return { city: head, state: US_STATES[name] };
      }
      break;
    }
  }
  const cut = text.lastIndexOf(" ");
  if (cut > 0) {
    const st = abbrOfToken(text.slice(cut + 1), false);
    const head = stripEdges(text.slice(0, cut));
    if (st && head && !isPrefix(head)) return { city: head, state: st };
  }
  return { city: text, state: "" };
}

// { abbr, name } for a stored state value: a codes value (read from
// codeMap.state[value]: options.abbr, else its name), a numeric string, a
// postal code or a state name.
const stateInfo = (value, codeMap) => {
  if (value == null || typeof value === "boolean") return { abbr: "", name: "" };
  let v = value;
  if (typeof v === "string") {
    // ASCII digits only, the same as the backend (Python's isdigit() also
    // takes "\u00b2", which int() rejects).
    const s = v.split(/\s+/).filter(Boolean).join(" ");
    if (!s) return { abbr: "", name: "" };
    if (!/^\d+$/.test(s)) {
      const abbr = stateOfText(s);
      if (abbr) return { abbr, name: ABBR_TO_NAME[abbr] };
      const key = s.replace(/\./g, "").trim();
      if (/^[A-Za-z]{2}$/.test(key)) return { abbr: key.toUpperCase(), name: "" };
      return { abbr: "", name: "" };
    }
    v = parseInt(s, 10);
  }
  if (typeof v !== "number" || !Number.isInteger(v) || v <= 0) return { abbr: "", name: "" };
  const row = codeMap?.state?.[v];
  if (!row) return { abbr: "", name: "" };
  const name = typeof row.name === "string" ? row.name.trim() : "";
  const rawAbbr = typeof row.options?.abbr === "string" ? row.options.abbr : "";
  let abbr = rawAbbr.replace(/\./g, "").trim().toUpperCase();
  if (!abbr) abbr = NAME_TO_ABBR[name.toLowerCase()] || "";
  return { abbr, name: name || ABBR_TO_NAME[abbr] || "" };
};

// The postal code for a stored state, "" when unknown.
export function stateAbbr(value, codeMap) {
  return stateInfo(value, codeMap).abbr;
}

// "Chandler az" shown with AZ -> "Chandler"; the city alone equal to the
// state -> "". Never cut down to a lone prefix: "Northern California" and
// "Port Washington" stay whole.
const dropStateSuffix = (city, abbr, name) => {
  const low = city.toLowerCase();
  for (const s0 of [abbr, name]) {
    const s = (s0 || "").toLowerCase();
    if (!s) continue;
    if (low === s) return "";
    if (low.endsWith(` ${s}`)) {
      const head = stripEdges(city.slice(0, city.length - s.length));
      if (head && !isPrefix(head)) return head;
    }
  }
  return city;
};

// True when the city is a region of the state shown with it ("Northern
// California" with CA): the state is not repeated after it.
const regionOf = (city, abbr, name) => {
  const low = city.toLowerCase();
  for (const s0 of [name, abbr]) {
    const s = (s0 || "").toLowerCase();
    if (s && low.endsWith(` ${s}`)) {
      const head = stripEdges(city.slice(0, city.length - s.length)).replace(/\./g, "").trim().toLowerCase();
      if (REGION_WORDS.has(head)) return true;
    }
  }
  return false;
};

const nameOf = (v) => {
  if (v && typeof v === "object") return typeof v.name === "string" ? v.name.trim() : "";
  return v ? String(v).trim() : "";
};

// { text, city, state } to show. Same precedence as the backend: the
// athlete's own homeTown, then athlete.city, then the Cerebro bio. A typed
// state wins over a stored code; a bio city only takes the bio's state. A
// homeTown that is only a state name is a town when a DIFFERENT state is
// stored ("Washington" + PA -> "Washington, PA"; "Arizona" + AZ stays).
export function hometownParts(athlete = {}, codeMap = {}, bio) {
  const a = athlete || {};
  const b = bio && typeof bio === "object" ? bio : (a.statistics?.cerebro?.bio || {});
  let storedInfo = null;
  const stored = () => {
    if (!storedInfo) storedInfo = stateInfo(a.state, codeMap);
    return storedInfo;
  };
  const ownParts = (raw) => {
    const parts = splitHometown(raw);
    if (parts.state && !parts.city) {
      const s = stored();
      if ((s.abbr || s.name) && s.abbr !== parts.state) {
        // A town named after a state, in the state stored with it.
        return { city: dropTrailingNoise(collapse(raw)), state: "" };
      }
    }
    return parts;
  };
  const own = ownParts(a.homeTown);
  const legacy = ownParts(a.city);
  const fromBio = splitHometown(nameOf(b.hometown));

  const bioState = () => {
    if (fromBio.state) return { abbr: fromBio.state, name: ABBR_TO_NAME[fromBio.state] || "" };
    const info = typeof b.state === "string" ? stateInfo(b.state, codeMap) : { abbr: "", name: "" };
    if (info.abbr || info.name) return info;
    return stateInfo(b.state_value, codeMap);
  };

  let city = "";
  let typed = "";
  let st = { abbr: "", name: "" };
  if (own.city || legacy.city) {
    city = own.city || legacy.city;
    typed = own.city ? own.state : own.state || legacy.state;
    if (typed) {
      st = { abbr: typed, name: ABBR_TO_NAME[typed] || "" };
    } else {
      st = stored();
      if (!(st.abbr || st.name) && fromBio.city && fromBio.city.toLowerCase() === city.toLowerCase()) {
        st = bioState();
      }
    }
  } else if (fromBio.city) {
    city = fromBio.city;
    typed = fromBio.state;
    st = bioState();
  } else {
    typed = own.state || legacy.state;
    st = typed ? { abbr: typed, name: ABBR_TO_NAME[typed] || "" } : stored();
  }

  if (city && !typed) {
    if (city.includes(",")) {
      // Kept as typed (non-US region): no state is glued on after it.
      st = { abbr: "", name: "" };
    } else if (st.abbr || st.name) {
      city = dropStateSuffix(city, st.abbr, st.name);
    }
  }

  const shown = st.abbr || st.name;
  let text = "";
  if (city && shown && !regionOf(city, st.abbr, st.name)) text = `${city}, ${shown}`;
  else if (city) text = city;
  else text = ABBR_TO_NAME[st.abbr] || st.name || st.abbr;
  return { text, city, state: shown };
}

// "Chandler, AZ", the city alone, the full state name when only a state is
// known, or "".
export function formatHometown(athlete, codeMap, bio) {
  return hometownParts(athlete, codeMap, bio).text;
}

// The place shown after the school on a Player Index card: the formatted
// hometown the list row carries ("Chandler, AZ"), else the state name the
// card had before ("Nevada").
export function cardPlace(athlete, stateName = "") {
  const hometown = typeof athlete?.hometown === "string" ? athlete.hometown.trim() : "";
  const fallback = typeof stateName === "string" ? stateName.trim() : "";
  return hometown || fallback;
}
